import { Component, OnInit, ViewChild, ElementRef, Input, Output, EventEmitter, OnDestroy, AfterViewInit, NgZone, HostListener, OnChanges, ViewChildren, QueryList } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { WhiteLabelService } from 'src/app/_service/white-label.service';
import { URLS } from 'src/app/constants/URLS';
import { getVideoThumbnail } from 'src/app/utils/media-url.utils';
import { DeviceService } from 'src/app/_service/tv-detect.service';
import { TVNavigationService, Region } from 'src/app/_service/tv-navigation.service';

interface MovieItem {
  vidLibId: string;
  title: string;
  watchedPercentage?: number;
  categoryId?: string;
  channelId?: string;
  series_id?: string;
  description?: string;
  duration?: string;
  genre?: string;
  year?: string;
  rating?: string;
  remainingTime?: string;
  [key: string]: any;
}

interface MovieHoverEvent {
  movie: MovieItem;
  event: MouseEvent;
}

interface MovieActionEvent {
  movie: MovieItem;
  action: 'remove' | 'play' | 'details';
}

@Component({
  selector: 'app-spread-carousel',
  templateUrl: './spread-carousel.component.html',
  styleUrls: ['./spread-carousel.component.css']
})
export class SpreadCarouselComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  
  @Input() movies: MovieItem[] = [];
  @Input() customCarouselOptions?: any;
  @Input() isCarousel: boolean = true;
  @Input() showRemoveButton: boolean = false;
  @Input() showWatchedPercentage: boolean = true;
  @Input() showHoverDetails: boolean = true;
  @Input() gridColumns: number = 6;
  @Input() componentType: 'continue-watching' | 'watchlist' | 'general' = 'continue-watching';
  @Input() rowId?: string;
  
  @Output() movieHover = new EventEmitter<MovieHoverEvent>();
  @Output() movieLeave = new EventEmitter<MovieItem>();
  @Output() movieAction = new EventEmitter<MovieActionEvent>();
  
  @ViewChild('owlCarousel', { static: false }) owlCarousel: ElementRef;
  @ViewChild('carouselTrack', { static: false }) carouselTrack: ElementRef;
  @ViewChildren('movieCard') cardRefs!: QueryList<ElementRef>;

  imgUrl:string = URLS.imgUrl;
  hoveredMovie: MovieItem | null = null;
  mobileMenuMovie: MovieItem | null = null;
  hoverTimeout: any;
  
  currentIndex = 0;
  translateX = 0;
  isAnimating = false;
  itemWidth = 0;
  itemsPerView = 1;
  maxIndex = 0;
  showNavigation = true;
  marginRight = 0;
  containerWidth = 0;
  isCarouselEnabled = true;

  isDragging = false;
  private startX = 0;
  private startY = 0;
  private currentX = 0;
  private currentY = 0;
  private lastX = 0;
  private lastY = 0;
  private initialTranslateX = 0;
  private startTime = 0;
  private lastTime = 0;
  private velocityX = 0;
  private velocityY = 0;
  private isHorizontalSwipe = false;
  private isVerticalSwipe = false;
  private swipeDecided = false;
  private touchStarted = false;
  private moved = false;
  private minSwipeDistance = 10;
  private snapThreshold = 0.3;
  private resizeObserver?: ResizeObserver;
  private animationFrameId: number = 0;

  // TV Navigation state
  private destroy$ = new Subject<void>();
  private generatedRegionId?: string;
  focusedCardIndex = -1;

  constructor(
    private ngZone: NgZone,
    private whitelabelService: WhiteLabelService,
    private device: DeviceService,
    private tvNav: TVNavigationService,
    private router: Router
    ) { }

  ngOnInit(): void {
    this.mergeOptions();
    this.updateCarouselView();
  }

  ngOnChanges(): void {
    if (this.movies && this.movies.length > 0 && this.owlCarousel) {
      setTimeout(() => {
        this.updateCarouselView();
        this.calculateDimensions();
      }, 50);
    }
  }

  ngAfterViewInit(): void {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.setupCarousel();
        setTimeout(() => {
          this.updateCarouselView();
          this.calculateDimensions();
        }, 100);
      });
    });

    // TV Navigation registration
    if (this.device.isTV()) {
      setTimeout(() => {
        this.registerWithNavigation();
      }, 100);

      this.cardRefs?.changes
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.registerWithNavigation();
        });

      this.tvNav.focus$
        .pipe(takeUntil(this.destroy$))
        .subscribe(focusKey => {
          const regionId = this.rowId || this.generatedRegionId;
          if (focusKey && regionId && focusKey.startsWith(`${regionId}:`)) {
            this.focusedCardIndex = parseInt(focusKey.split(':')[1], 10);
            // Scroll this carousel's track so the focused card is visible
            this.scrollToFocusedCard(this.focusedCardIndex);
          } else {
            this.focusedCardIndex = -1;
          }
        });
    }
  }

  ngOnDestroy(): void {
    // TV Navigation cleanup
    this.destroy$.next();
    this.destroy$.complete();

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    this.removeEventListeners();
    clearTimeout(this.hoverTimeout);

    if (this.device.isTV()) {
      const regionId = this.rowId || this.generatedRegionId;
      if (regionId) {
        this.tvNav.unregisterRegion(regionId);
      }
    }
  }

  private mergeOptions(): void {
    if (this.customCarouselOptions) {
      this.carouselOptions = { ...this.carouselOptions, ...this.customCarouselOptions };
    }
  }

  private setupCarousel(): void {
    if (this.isCarousel && this.isCarouselEnabled) {
      this.calculateDimensions();
      this.setupResizeObserver();
      this.setupEventListeners();
    }
  }

  private registerWithNavigation(): void {
    if (!this.cardRefs || this.cardRefs.length === 0) return;

    // Generate unique ID if not provided
    if (!this.rowId && !this.generatedRegionId) {
      this.generatedRegionId = `spread-carousel-${Date.now()}`;
    }

    const regionId = this.rowId || this.generatedRegionId;
    if (!regionId) return;

    const region: Region = {
      id: regionId,
      type: 'row',
      elementRef: this.owlCarousel,
      focusableItems: this.cardRefs.toArray()
    };
    this.tvNav.registerRegion(region);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this.updateCarouselView();
    requestAnimationFrame(() => {
      this.calculateDimensions();
    });
  }

  updateCarouselView() {
    if (!this.isCarousel) {
      this.isCarouselEnabled = false;
      return;
    }
    const screenWidth = window.innerWidth;
    const minItemsForCurrentScreen = this.getMinItemsForScreen(screenWidth);
    this.isCarouselEnabled = this.movies?.length > minItemsForCurrentScreen;
    if (this.isCarouselEnabled) {
      this.updateResponsiveSettings();
      requestAnimationFrame(() => {
        this.calculateDimensions();
      });
    }
  }

  private updateResponsiveSettings() {
    const screenWidth = window.innerWidth;
    const responsiveConfig = this.getResponsiveConfigForScreen(screenWidth);
    
    this.itemsPerView = responsiveConfig.items;
    this.marginRight = responsiveConfig.margin || 0;
    this.showNavigation = responsiveConfig.nav !== false && this.carouselOptions.nav !== false;
  }

  private getResponsiveConfigForScreen(screenWidth: number) {
    const breakpoints = Object.keys(this.carouselOptions.responsive)
      .map(key => parseInt(key))
      .sort((a, b) => b - a);
    
    for (const breakpoint of breakpoints) {
      if (screenWidth >= breakpoint) {
        return this.carouselOptions.responsive[breakpoint];
      }
    }
    
    const smallestBreakpoint = Math.min(...breakpoints);
    return this.carouselOptions.responsive[smallestBreakpoint];
  }

  private calculateDimensions() {
    if (!this.owlCarousel || !this.isCarouselEnabled || !this.isCarousel) return;
    if (this.owlCarousel.nativeElement.offsetWidth === 0) {
      setTimeout(() => this.calculateDimensions(), 50);
      return;
    }
    this.containerWidth = this.owlCarousel.nativeElement.offsetWidth;
    const totalItems = this.movies.length;
    if (totalItems === 0) return;
    const totalMarginSpace = this.marginRight * (this.itemsPerView - 1);
    const availableSpace = this.containerWidth - totalMarginSpace;
    this.itemWidth = Math.floor(availableSpace / this.itemsPerView);
    if (this.itemWidth < 50) {
      this.itemWidth = Math.floor(this.containerWidth / this.itemsPerView);
    }
    this.maxIndex = Math.max(0, totalItems - this.itemsPerView);
    if (this.currentIndex > this.maxIndex) {
      this.currentIndex = this.maxIndex;
    }
    this.updateTransform();
  }

  private updateTransform() {
    const newTranslateX = -(this.currentIndex * (this.itemWidth + this.marginRight));
    this.translateX = newTranslateX;
  }

  /**
   * TV: Hotstar-style — always keep the focused card as the first visible card.
   * When pressing RIGHT, the carousel slides left so the new card is at position 0.
   */
  private scrollToFocusedCard(cardIndex: number) {
    if (!this.isCarouselEnabled || this.itemWidth === 0) return;

    // Always position the focused card as the first visible (leftmost) card
    this.currentIndex = Math.min(cardIndex, this.maxIndex);
    this.isAnimating = true;
    this.updateTransform();
    setTimeout(() => this.isAnimating = false, 350);
  }

  private setupResizeObserver() {
    if (typeof ResizeObserver !== 'undefined' && this.owlCarousel) {
      this.resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.contentRect.width > 0) {
            requestAnimationFrame(() => {
              if (this.isCarouselEnabled && this.isCarousel) {
                this.updateCarouselView();
                this.calculateDimensions();
              }
            });
          }
        }
      });
      this.resizeObserver.observe(this.owlCarousel.nativeElement);
    }
  }

  private setupEventListeners() {
    if (this.carouselTrack) {
      const element = this.carouselTrack.nativeElement;
      element.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: false });
      element.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: false });
      element.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: false });
      element.addEventListener('touchcancel', this.onTouchEnd.bind(this), { passive: false });
      element.addEventListener('mousedown', this.onMouseDown.bind(this), { passive: false });
      document.addEventListener('mousemove', this.onMouseMove.bind(this), { passive: false });
      document.addEventListener('mouseup', this.onMouseUp.bind(this), { passive: false });
    }
  }

  private removeEventListeners() {
    if (this.carouselTrack) {
      const element = this.carouselTrack.nativeElement;
      element.removeEventListener('touchstart', this.onTouchStart.bind(this));
      element.removeEventListener('touchmove', this.onTouchMove.bind(this));
      element.removeEventListener('touchend', this.onTouchEnd.bind(this));
      element.removeEventListener('touchcancel', this.onTouchEnd.bind(this));
      element.removeEventListener('mousedown', this.onMouseDown.bind(this));
      document.removeEventListener('mousemove', this.onMouseMove.bind(this));
      document.removeEventListener('mouseup', this.onMouseUp.bind(this));
    }
  }

  next() {
    if (this.currentIndex < this.maxIndex && !this.isAnimating) {
      this.currentIndex++;
      this.smoothSlide();
    }
  }

  prev() {
    if (this.currentIndex > 0 && !this.isAnimating) {
      this.currentIndex--;
      this.smoothSlide();
    }
  }

  private smoothSlide() {
    this.isAnimating = true;
    this.updateTransform();
    
    setTimeout(() => {
      this.isAnimating = false;
    }, 300);
  }

  onTouchStart(event: TouchEvent) {
    if (!this.carouselOptions.touchDrag || !this.isCarouselEnabled || !this.isCarousel) return;
    if (event.touches.length === 1) {
      const touch = event.touches[0];
      this.touchStarted = true;
      this.moved = false;
      this.swipeDecided = false;
      this.isHorizontalSwipe = false;
      this.isVerticalSwipe = false;
      this.startX = touch.clientX;
      this.startY = touch.clientY;
      this.currentX = touch.clientX;
      this.currentY = touch.clientY;
      this.lastX = touch.clientX;
      this.lastY = touch.clientY;
      this.initialTranslateX = this.translateX;
      this.startTime = Date.now();
      this.lastTime = this.startTime;
      this.velocityX = 0;
      this.velocityY = 0;
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = 0;
      }
    }
  }

  onTouchMove(event: TouchEvent) {
    if (!this.touchStarted || event.touches.length !== 1) return;
    const touch = event.touches[0];
    const now = Date.now();
    this.currentX = touch.clientX;
    this.currentY = touch.clientY;
    const deltaX = this.currentX - this.startX;
    const deltaY = this.currentY - this.startY;
    if (!this.swipeDecided && (Math.abs(deltaX) > this.minSwipeDistance || Math.abs(deltaY) > this.minSwipeDistance)) {
      this.isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
      this.isVerticalSwipe = Math.abs(deltaY) > Math.abs(deltaX);
      this.swipeDecided = true;
    }
    if (this.swipeDecided && this.isHorizontalSwipe) {
      this.moved = true;
      this.isDragging = true;
      if (now - this.lastTime > 16) {
        this.velocityX = (this.currentX - this.lastX) / (now - this.lastTime);
        this.lastX = this.currentX;
        this.lastTime = now;
      }
      this.updateCarouselPosition(deltaX);
      event.preventDefault();
      event.stopPropagation();
    }
    else if (this.swipeDecided && this.isVerticalSwipe) {
      return;
    }
  }

  onTouchEnd(event: TouchEvent) {
    if (!this.touchStarted) return;
    this.touchStarted = false;
    if (this.isDragging && this.moved) {
      this.endDrag();
      event.preventDefault();
      event.stopPropagation();
    }
    this.resetDragState();
  }

  onMouseDown(event: MouseEvent) {
    if (!this.carouselOptions.mouseDrag || !this.isCarouselEnabled || !this.isCarousel) return;
    this.isDragging = true;
    this.startX = event.clientX;
    this.currentX = event.clientX;
    this.lastX = event.clientX;
    this.initialTranslateX = this.translateX;
    this.startTime = Date.now();
    this.lastTime = this.startTime;
    this.velocityX = 0;
    event.preventDefault();
    event.stopPropagation();
  }

  onMouseMove(event: MouseEvent) {
    if (!this.isDragging) return;
    const now = Date.now();
    this.currentX = event.clientX;
    if (now - this.lastTime > 16) {
      this.velocityX = (this.currentX - this.lastX) / (now - this.lastTime);
      this.lastX = this.currentX;
      this.lastTime = now;
    }
    const deltaX = this.currentX - this.startX;
    this.updateCarouselPosition(deltaX);
    event.preventDefault();
  }

  onMouseUp(event: MouseEvent) {
    if (!this.isDragging) return;
    this.endDrag();
    this.resetDragState();
  }

  private updateCarouselPosition(deltaX: number) {
    let newTranslateX = this.initialTranslateX + deltaX;
    const maxTranslateX = 0;
    const minTranslateX = -((this.itemWidth + this.marginRight) * this.maxIndex);
    if (newTranslateX > maxTranslateX) {
      newTranslateX = maxTranslateX + (newTranslateX - maxTranslateX) * 0.3;
    } else if (newTranslateX < minTranslateX) {
      newTranslateX = minTranslateX + (newTranslateX - minTranslateX) * 0.3;
    }
    this.ngZone.runOutsideAngular(() => {
      this.translateX = newTranslateX;
    });
  }

  private endDrag() {
    this.isDragging = false;
    const deltaX = this.currentX - this.startX;
    const duration = Date.now() - this.startTime;
    const itemSize = this.itemWidth + this.marginRight;
    const currentPosition = -this.translateX / itemSize;
    if (Math.abs(this.velocityX) > 1.0 && duration < 300) {
      const velocityFactor = Math.abs(this.velocityX) * 2;
      const itemsToMove = Math.min(Math.ceil(velocityFactor), 3);
      if (this.velocityX > 0) {
        this.currentIndex = Math.max(0, Math.floor(currentPosition) - itemsToMove + 1);
      } else {
        this.currentIndex = Math.min(this.maxIndex, Math.floor(currentPosition) + itemsToMove);
      }
    } else {
      this.currentIndex = Math.round(currentPosition);
    }
    this.currentIndex = Math.max(0, Math.min(this.currentIndex, this.maxIndex));
    this.animateToPosition();
  }

  private animateToPosition() {
    const targetX = -(this.currentIndex * (this.itemWidth + this.marginRight));
    this.isAnimating = true;
    this.translateX = targetX;
    setTimeout(() => {
      this.isAnimating = false;
    }, 300);
  }

  private resetDragState() {
    this.isDragging = false;
    this.touchStarted = false;
    this.moved = false;
    this.swipeDecided = false;
    this.isHorizontalSwipe = false;
    this.isVerticalSwipe = false;
    this.velocityX = 0;
    this.velocityY = 0;
  }

  getMinItemsForScreen(screenWidth: number): number {
    const breakpoints = Object.keys(this.carouselOptions.responsive).map(key => parseInt(key)).sort((a, b) => b - a); 
    for (const breakpoint of breakpoints) {
      if (screenWidth >= breakpoint) {
        return this.carouselOptions.responsive[breakpoint].items;
      }
    }
    const smallestBreakpoint = Math.min(...breakpoints);
    return this.carouselOptions.responsive[smallestBreakpoint].items;
  }

  getImageUrl(movie: MovieItem): string {
    if (movie?.series_id) {
      return `${this.imgUrl}SER_${movie?.series_id}_thumb.jpg`;
    }
    return getVideoThumbnail(movie);
  }

  onMovieHover(movie: MovieItem, event: MouseEvent): void {
    clearTimeout(this.hoverTimeout);
    this.hoverTimeout = setTimeout(() => {
      this.hoveredMovie = movie;
      this.movieHover.emit({ movie, event });
    }, 300); 
  }

  onMovieLeave(movie: MovieItem): void {
    clearTimeout(this.hoverTimeout);
    this.hoverTimeout = setTimeout(() => {
      this.hoveredMovie = null;
      this.movieLeave.emit(movie);
    }, 100);
  }

  onMovieAction(movie: MovieItem, action: 'remove' | 'play' | 'details', event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.mobileMenuMovie = null;
    if (action === 'details') {
      this.router.navigateByUrl(this.getRouterLink(movie));
      return;
    }
    this.movieAction.emit({ movie, action });
  }

  toggleMobileMenu(movie: MovieItem, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.mobileMenuMovie = this.mobileMenuMovie?.vidLibId === movie.vidLibId ? null : movie;
  }

  closeMobileMenu(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.mobileMenuMovie = null;
  }

  onMovieClick(movie: MovieItem): void {
    if (!this.isDragging) {
    }
  }

  getRouterLink(movie: any): string {
    let route;
    if(movie?.series_id){
      route = this.whitelabelService.getPrefixedRoute('shows', movie.series_id);
    }else{
      route = this.whitelabelService.getPrefixedRoute('movies', movie.title, movie.vidLibId);
    }
    return route;
  }

  getQueryParams(movie: MovieItem): any {
    return {
      id: movie.vidLibId,
      category: movie.categoryId,
      channel: movie.channelId,
      series_id: movie.series_id
    };
  }

  getGridClass(): string {
    return `grid-cols-${this.gridColumns}`;
  }

  formatDuration(duration: string): string {
    if (!duration) return '';
    const minutes = parseInt(duration);
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  }

  getWatchedPercentageText(percentage: number): string {
    if (percentage >= 100) return 'Completed';
    if (percentage >= 90) return 'Almost done';
    if (percentage >= 50) return 'Half watched';
    if (percentage >= 10) return 'Started';
    return 'New';
  }

  trackByMovie(index: number, movie: MovieItem): string {
    return movie?.vidLibId;
  }

  carouselOptions = {
    loop: false,
    mouseDrag: true,
    touchDrag: true,
    pullDrag: true,
    dots: false,
    navSpeed: 600,
    nav: true,
    navText: ['<i class="fa fa-chevron-left"></i>', '<i class="fa fa-chevron-right"></i>'],
    margin: 8,
    autoplay: false,
    responsive: {
      0: {
        items: 2,
        nav: false,
        margin: 3,
      },
      400: {
        items: 2.05,
        nav: false,
        margin: 6,
      },
      600: {
        items: 3,
        nav: false,
        margin: 3,
      },
      800: {
        items: 5,
        nav: true,
        margin: 8
      },
      1000: {
        items: 5,
        nav: true,
        margin: 8
      },
      1200: {
        items: 5,
        nav: true,
        margin: 8
      },
      1600: {
        items: 6,
        nav: true,
        margin: 8
      }
    }
  };
}